<script setup lang="ts">
/**
 * Body of /alternatives/<slug>: one competitor, compared on facts.
 *
 * EU comparative advertising (Directive 2006/114/EC) allows naming a
 * competitor only when the comparison is objective, verifiable and about
 * material features. So every competitor line carries its source link and the
 * date it was checked, our side comes from constants/pricing.ts and the
 * product facts, and each page says honestly when the competitor is the
 * better choice. No ratings, no opinions about their product.
 */
import { JOB_PACK_DAYS } from '~/constants/pricing'
import { COMPARE_ROWS, withOurPrices, type Competitor } from '~/constants/competitors'
import { CANCEL_GUIDE_SLUGS } from '~/constants/cancelGuides'

const props = defineProps<{ competitor: Competitor }>()

const { t, locale } = useI18n()

const hasCancelGuide = computed(() => CANCEL_GUIDE_SLUGS.includes(props.competitor.slug))

const checkedLabel = computed(() =>
  new Date(`${props.competitor.checked}T12:00:00Z`).toLocaleDateString(({ cs: 'cs-CZ', de: 'de-DE', pl: 'pl-PL' } as Record<string, string>)[locale.value] ?? 'en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
)

const { prices, format } = usePricing().pricing.value
const priceParams = {
  export: format(prices.exportCredit),
  pack: format(prices.jobPack),
  yearly: format(prices.yearly),
  days: JOB_PACK_DAYS,
}
</script>

<template>
  <div class="sofax">
    <!-- The answer -->
    <section class="bg-white dark:bg-gray-950">
      <div class="section-space-sm">
        <div class="shell">
          <div class="mx-auto max-w-3xl rounded-[0.625rem] border-l-4 border-accent bg-surface p-[1.875rem] dark:bg-gray-800">
            <div class="mb-3 text-sm font-semibold uppercase tracking-wide text-muted dark:text-gray-400">
              {{ t('alternatives.page.shortLabel') }}
            </div>
            <p class="text-lg">{{ withOurPrices(competitor.short) }}</p>
            <p class="mt-4 text-sm text-muted dark:text-gray-400">{{ t('alternatives.page.checked', { name: competitor.name, date: checkedLabel }) }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Their pricing, line by line, each with its source -->
    <section class="bg-white dark:bg-gray-950">
      <div class="section-space-bottom">
        <div class="shell">
          <SofaxSectionHeading
            :eyebrow="t('alternatives.page.pricing.eyebrow')"
            :title="t('alternatives.page.pricing.title', { name: competitor.name })"
            width="max-w-4xl"
          />
          <div class="mx-auto max-w-4xl overflow-x-auto">
            <table class="w-full border-collapse text-left text-base">
              <thead>
                <tr>
                  <th
                    v-for="col in ['plan', 'price', 'renews']"
                    :key="col"
                    scope="col"
                    class="border border-line bg-surface px-4 py-3 font-title font-semibold text-ink dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  >
                    {{ t(`alternatives.page.pricing.head.${col}`) }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="plan in competitor.pricing" :key="plan.plan">
                  <th scope="row" class="border border-line px-4 py-3 align-top font-semibold text-ink dark:border-gray-700 dark:text-white">
                    {{ plan.plan }}
                  </th>
                  <td class="border border-line px-4 py-3 align-top dark:border-gray-700">{{ plan.price }}</td>
                  <td class="border border-line px-4 py-3 align-top dark:border-gray-700">
                    {{ plan.renews }}
                    <a :href="plan.source" target="_blank" rel="noopener nofollow" class="link ml-1 text-sm">{{ t('alternatives.page.source') }}</a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="mx-auto mt-8 grid max-w-4xl grid-cols-1 gap-4 md:grid-cols-2">
            <div class="rounded-[0.625rem] bg-surface p-[1.875rem] dark:bg-gray-800">
              <h3 class="!text-xl">{{ t('alternatives.page.free.title', { name: competitor.name }) }}</h3>
              <p class="mt-3 text-base">
                {{ competitor.free.text }}
                <a :href="competitor.free.source" target="_blank" rel="noopener nofollow" class="link text-sm">{{ t('alternatives.page.source') }}</a>
              </p>
            </div>
            <div class="rounded-[0.625rem] bg-surface p-[1.875rem] dark:bg-gray-800">
              <h3 class="!text-xl">{{ t('alternatives.page.cancel.title', { name: competitor.name }) }}</h3>
              <p class="mt-3 text-base">
                {{ competitor.cancel.text }}
                <a :href="competitor.cancel.source" target="_blank" rel="noopener nofollow" class="link text-sm">{{ t('alternatives.page.source') }}</a>
              </p>
              <!-- English-only page, so a plain link in every language -->
              <p v-if="hasCancelGuide" class="mt-4">
                <NuxtLink :to="`/how-to-cancel/${competitor.slug}`" class="link">{{ t('alternatives.page.cancel.steps') }} →</NuxtLink>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Side by side -->
    <section class="surface-alt">
      <div class="section-space">
        <div class="shell">
          <SofaxSectionHeading
            :eyebrow="t('alternatives.page.compare.eyebrow')"
            :title="t('alternatives.page.compare.title', { name: competitor.name })"
            :text="t('alternatives.page.compare.lead')"
            width="max-w-4xl"
          />
          <div class="mx-auto max-w-5xl overflow-x-auto">
            <table class="w-full border-collapse bg-white text-left text-base dark:bg-gray-900">
              <thead>
                <tr>
                  <th scope="col" class="border border-line bg-surface px-4 py-3 font-title font-semibold text-ink dark:border-gray-700 dark:bg-gray-800 dark:text-white" />
                  <th scope="col" class="border border-line bg-surface px-4 py-3 font-title font-semibold text-ink dark:border-gray-700 dark:bg-gray-800 dark:text-white">
                    {{ competitor.name }}
                  </th>
                  <th scope="col" class="border border-line bg-surface px-4 py-3 font-title font-semibold text-ink dark:border-gray-700 dark:bg-gray-800 dark:text-white">
                    InMyResume
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in COMPARE_ROWS" :key="row">
                  <th scope="row" class="border border-line px-4 py-3 align-top font-semibold text-ink dark:border-gray-700 dark:text-white">
                    {{ t(`alternatives.page.compare.rows.${row}`) }}
                  </th>
                  <td class="border border-line px-4 py-3 align-top dark:border-gray-700">
                    {{ competitor.features[row].value }}
                    <a
                      v-if="competitor.features[row].source"
                      :href="competitor.features[row].source"
                      target="_blank"
                      rel="noopener nofollow"
                      class="link ml-1 text-sm"
                    >{{ t('alternatives.page.source') }}</a>
                  </td>
                  <td class="border border-line px-4 py-3 align-top dark:border-gray-700">
                    {{ t(`alternatives.us.${row}`, priceParams) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>

    <!-- When they are the better choice: said plainly -->
    <section class="bg-white dark:bg-gray-950">
      <div class="section-space">
        <div class="shell">
          <div class="mx-auto grid max-w-4xl grid-cols-1 gap-6 md:grid-cols-2">
            <div class="rounded-[0.625rem] bg-surface p-[1.875rem] dark:bg-gray-800">
              <h2 class="!text-2xl">{{ t('alternatives.page.theirs.title', { name: competitor.name }) }}</h2>
              <ul class="mt-4 flex flex-col gap-3">
                <li v-for="(line, i) in competitor.betterFor" :key="i" class="flex items-start gap-3 text-base">
                  <i class="bi bi-check-lg mt-0.5 shrink-0 text-lg text-muted" />
                  <span>{{ line }}</span>
                </li>
              </ul>
            </div>
            <div class="rounded-[0.625rem] border-l-4 border-accent bg-surface p-[1.875rem] dark:bg-gray-800">
              <h2 class="!text-2xl">{{ t('alternatives.page.ours.title') }}</h2>
              <ul class="mt-4 flex flex-col gap-3">
                <li v-for="id in ['once', 'free', 'noAccount', 'facts']" :key="id" class="flex items-start gap-3 text-base">
                  <i class="bi bi-check-lg mt-0.5 shrink-0 text-lg text-accent" />
                  <span>{{ t(`alternatives.page.ours.items.${id}`, priceParams) }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- The one offer -->
    <section class="surface-alt">
      <div class="section-space">
        <div class="shell">
          <div class="mx-auto max-w-3xl text-center">
            <h2 class="!text-3xl md:!text-[2.5rem]">{{ t('alternatives.page.next.title') }}</h2>
            <p class="mt-5 text-lg">{{ t('alternatives.page.next.p1') }}</p>
            <div class="mt-8">
              <SofaxBtn to="/free-cv-builder" :label="t('alternatives.page.next.cta')" variant="accent" />
            </div>
            <p class="mt-6">
              <LocaleLink to="/alternatives" class="link">{{ t('alternatives.page.next.hubLink') }} →</LocaleLink>
            </p>
            <p class="mt-10 text-sm text-muted dark:text-gray-400">{{ t('alternatives.page.trademark', { name: competitor.name }) }}</p>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
